import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import apiBaseUrl from './apiBaseUrl';

const UserContext = createContext();

const MAX_ATTEMPTS = 4;
const RETRY_DELAY_MS = 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function UserProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadUserInfo = async (account) => {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const response = await fetch(`${apiBaseUrl}/api/health`, {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`/api/health returned ${response.status}`);
      }
      const data = await response.json();
      return data.user;
    };

    // Right after the sign-in redirect the first token or API call can fail (seen in Safari); retry before giving up.
    const fetchUserInfo = async (account) => {
      setLoading(true);
      setError(null);
      let lastError = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS && !cancelled; attempt++) {
        try {
          const user = await loadUserInfo(account);
          if (!cancelled) {
            setUserInfo(user);
            setLoading(false);
          }
          return;
        } catch (e) {
          lastError = e;
          console.error(`Failed to fetch user info (attempt ${attempt}/${MAX_ATTEMPTS}):`, e);
          if (attempt < MAX_ATTEMPTS) {
            await sleep(RETRY_DELAY_MS * attempt);
          }
        }
      }
      if (!cancelled) {
        setError(lastError);
        setLoading(false);
      }
    };

    // Use getAllAccounts() as fallback because on first render after redirect,
    // the accounts array from useMsal may be momentarily empty before MSAL
    // processes the redirect response, causing a premature loading=false state.
    const availableAccounts = accounts.length > 0 ? accounts : instance.getAllAccounts();
    if (availableAccounts.length > 0) {
      const account = accounts[0] || instance.getActiveAccount() || availableAccounts[0];
      fetchUserInfo(account);
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [instance, accounts, reloadCounter]);

  const reload = () => setReloadCounter((value) => value + 1);

  return (
    <UserContext.Provider value={{ userInfo, loading, error, reload }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}
